import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import media from '../style/media';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Flex } from '../components/shared/Flex';
import { useGetPatientByIdQuery, useInsertLicenseUsageMutation, useCreateDeviceUserMutation } from '../services/patientApi';
import { useGetOrganizationByIdQuery } from '../services/organizationApi';
import { StorageKeys } from '../utils/storage';
import { User, Calendar, Weight, Ruler, Cigarette, Lock, Mail, Phone, Loader2 } from 'lucide-react';

import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard } from '../style/layout';

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  width: 100%;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  padding-left: 0.25rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 1rem;
    color: #94a3b8;
    pointer-events: none;
    z-index: 10;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.875rem 0.875rem 0.875rem 3rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  color: #0f172a;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.875rem 0.875rem 0.875rem 3rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  color: #0f172a;
  appearance: none;
  cursor: pointer;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
`;

const PrivacyNote = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  font-size: 0.85rem;
  color: #475569;
  background: #f8fafc;
  padding: 1rem;
  border-radius: 16px;
  margin: 1.5rem 0;
  line-height: 1.5;
  border: 1px solid #f1f5f9;

  svg {
    margin-top: 2px;
  }
`;

const StartButton = styled.button<{ isComplete: boolean }>`
  width: 100%;
  padding: 1rem;
  background: ${props => props.isComplete
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : '#e2e8f0'};
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: ${props => props.isComplete ? 'pointer' : 'not-allowed'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.isComplete ? '0 4px 12px rgba(102, 126, 234, 0.25)' : 'none'};

  &:hover {
    ${props => props.isComplete && `
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.35);
      opacity: 0.95;
    `}
  }

  &:active {
    ${props => props.isComplete && `
      transform: translateY(0);
    `}
  }
`;

const EnhancedScan = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    id_number: '',
    gender: '',
    age: '',
    weight: '',
    height: '',
    smoking: ''
  });

  const [scanFor, setScanFor] = useState<'self' | 'other'>('self');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [createDeviceUser] = useCreateDeviceUserMutation();
  const [insertLicenseUsage] = useInsertLicenseUsageMutation();
  const cameras = useCameras();

  const pid = useMemo(() => {
    const patientId = localStorage.getItem(StorageKeys.PATIENT_ID);
    return (patientId === 'null' || patientId === 'undefined' || !patientId) ? '' : patientId;
  }, []);
  const currentOrgId = useMemo(() => {
    const orgId = localStorage.getItem(StorageKeys.ORG_ID);
    return (orgId === 'null' || orgId === 'undefined' || !orgId) ? '' : orgId;
  }, []);
  
  const { data: patientData } = useGetPatientByIdQuery(pid, { skip: !pid });
  const { data: orgData } = useGetOrganizationByIdQuery(currentOrgId, { skip: !currentOrgId });

  const requiredFieldsSelf = ['gender', 'age', 'weight', 'height', 'smoking'];
  const requiredFieldsOther = ['name', 'email', 'phone', ...requiredFieldsSelf];

  const isFormComplete = scanFor === 'self'
    ? requiredFieldsSelf.every(k => formData[k as keyof typeof formData] !== '')
    : requiredFieldsOther.every(k => formData[k as keyof typeof formData] !== '');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEnhancedMeasurement = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let deviceUserId: string | undefined;
      let organizationId = currentOrgId;

      const payloadExt = {
        age: Number(formData.age),
        sex: formData.gender === 'male' ? 'Male' : formData.gender === 'female' ? 'Female' : 'Unspecified',
        height: Number(formData.height),
        weight: Number(formData.weight),
        smoking_status: formData.smoking === 'smoker' ? 'Smoker' : formData.smoking === 'non-smoker' ? 'Non-smoker' : 'Unspecified'
      };

      const isPatientSelfScan = !!pid && scanFor === 'self';

      if (isPatientSelfScan) {
        deviceUserId = undefined;
        localStorage.removeItem(StorageKeys.DEVICE_USER_ID);
      } else if (scanFor === 'other') {
        const deviceUserResponse = await createDeviceUser({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          id_number: formData.id_number,
          patient_id: pid || undefined,
          ...payloadExt
        }).unwrap();

        if (deviceUserResponse.success) {
          deviceUserId = deviceUserResponse.data.id.toString();
          organizationId = deviceUserResponse.data.organization_id;
          localStorage.setItem(StorageKeys.DEVICE_USER_ID, deviceUserId);
        }
      } else {
        const selfName = orgData?.organization?.name || 'Organization User';
        const selfEmail = orgData?.organization?.email || '';
        const selfPhone = orgData?.organization?.phone || '';

        const deviceUserResponse = await createDeviceUser({
          name: selfName,
          email: selfEmail,
          phone: selfPhone,
          patient_id: pid || undefined,
          ...payloadExt
        }).unwrap();

        if (deviceUserResponse.success) {
          deviceUserId = deviceUserResponse.data.id.toString();
          organizationId = deviceUserResponse.data.organization_id;
          localStorage.setItem(StorageKeys.DEVICE_USER_ID, deviceUserId);
        }
      }

      const licenseId = patientData?.patient?.license_id || orgData?.organization?.license_id;
      const accessCode = patientData?.patient?.access_code;

      if (licenseId) {
        let usagePayload: any;
        if (isPatientSelfScan) {
          usagePayload = {
            license_id: licenseId,
            patient_id: pid,
            access_code: accessCode,
          };
        } else {
          usagePayload = {
            license_id: licenseId,
          };
          if (!pid) {
            usagePayload.organization_id = organizationId;
          }
          if (deviceUserId) {
            usagePayload.device_user_id = deviceUserId;
          }
        }
        const usageResponse = await insertLicenseUsage(usagePayload).unwrap();

        if (usageResponse?.licenseUsage?.id) {
          localStorage.setItem(StorageKeys.LICENSE_USAGE_ID, usageResponse.licenseUsage.id);
        }
      }

      navigate('/camera-scan', { state: { ...formData, isEnhanced: true, isFullScan: true, scanFor } });
    } catch (error) {
      console.error('Error calling license usage API:', error);
      navigate('/camera-scan', { state: { ...formData, isEnhanced: true, isFullScan: true, scanFor } });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />

      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />

      <ContentWrapper>
        <GlassCard>
          <Header>
            <Title>Configure Enhanced Scan</Title>
            <Subtitle>Enter your details for advanced health metrics calculation</Subtitle>
          </Header>

          <form onSubmit={handleEnhancedMeasurement}>
            <FormField>
              <FormLabel>Scan For</FormLabel>
              <InputWrapper>
                <User />
                <FormSelect
                  value={scanFor}
                  onChange={(e) => setScanFor(e.target.value as 'self' | 'other')}
                >
                  <option value="self">Self</option>
                  <option value="other">Others</option>
                </FormSelect>
              </InputWrapper>
            </FormField>

            {scanFor === 'other' && (
              <>
                <FormField>
                  <FormLabel>Full Name</FormLabel>
                  <InputWrapper>
                    <User />
                    <FormInput
                      type="text"
                      name="name"
                      placeholder="Enter full name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required={scanFor === 'other'}
                    />
                  </InputWrapper>
                </FormField>

                <FormField>
                  <FormLabel>Email</FormLabel>
                  <InputWrapper>
                    <Mail />
                    <FormInput
                      type="email"
                      name="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required={scanFor === 'other'}
                    />
                  </InputWrapper>
                </FormField>

                <FormField>
                  <FormLabel>Phone Number</FormLabel>
                  <InputWrapper>
                    <Phone />
                    <FormInput
                      type="tel"
                      name="phone"
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required={scanFor === 'other'}
                    />
                  </InputWrapper>
                </FormField>

                <FormField>
                  <FormLabel>ID Number</FormLabel>
                  <InputWrapper>
                    <User />
                    <FormInput
                      type="text"
                      name="id_number"
                      placeholder="Enter ID number (optional)"
                      value={formData.id_number}
                      onChange={handleInputChange}
                    />
                  </InputWrapper>
                </FormField>
              </>
            )}

            <FormField>
              <FormLabel>Gender</FormLabel>
              <InputWrapper>
                <User />
                <FormSelect
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </FormSelect>
              </InputWrapper>
            </FormField>

            <FormField>
              <FormLabel>Age (years)</FormLabel>
              <InputWrapper>
                <Calendar />
                <FormInput
                  type="number"
                  name="age"
                  min="18"
                  max="120"
                  placeholder="Enter your age"
                  value={formData.age}
                  onChange={handleInputChange}
                  required
                />
              </InputWrapper>
            </FormField>

            <FormField>
              <FormLabel>Weight (kg)</FormLabel>
              <InputWrapper>
                <Weight />
                <FormInput
                  type="number"
                  name="weight"
                  min="30"
                  max="300"
                  placeholder="Enter weight in kg"
                  value={formData.weight}
                  onChange={handleInputChange}
                  required
                />
              </InputWrapper>
            </FormField>

            <FormField>
              <FormLabel>Height (cm)</FormLabel>
              <InputWrapper>
                <Ruler />
                <FormInput
                  type="number"
                  name="height"
                  min="100"
                  max="250"
                  placeholder="Enter height in cm"
                  value={formData.height}
                  onChange={handleInputChange}
                  required
                />
              </InputWrapper>
            </FormField>

            <FormField>
              <FormLabel>Smoking Status</FormLabel>
              <InputWrapper>
                <Cigarette />
                <FormSelect
                  name="smoking"
                  value={formData.smoking}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Do you smoke?</option>
                  <option value="non-smoker">Non-smoker</option>
                  <option value="smoker">Smoker</option>
                </FormSelect>
              </InputWrapper>
            </FormField>

            <PrivacyNote>
              <Lock size={18} />
              <span>
                <strong>Confidential:</strong> Your data is used exclusively for heart age analysis and ASCVD risk scoring.
              </span>
            </PrivacyNote>

            <StartButton
              type="submit"
              disabled={!isFormComplete || isSubmitting}
              isComplete={isFormComplete && !isSubmitting}
            >
              {isSubmitting ? (
                <Flex gap="0.5rem" align="center" justify="center">
                  <Loader2 className="animate-spin" size={20} />
                  <span>Processing...</span>
                </Flex>
              ) : (
                'Start Enhanced Scan'
              )}
            </StartButton>
          </form>
        </GlassCard>
      </ContentWrapper>
    </PageContainer>
  );
};

export default EnhancedScan;